import { formatEwcDisplayCode } from "@/components/Forms/EwcCodeInput";
import type { WasteMovementDefaultMode } from "@/customTypings/wasteMovement/WasteMovementDefaults";

export type GeneralDefaultLookupItem = {
    id: string;
    code: string;
    description: string;
};

export type GeneralDefaultPreviewInput = {
    fieldKey: string;
    mode: WasteMovementDefaultMode;
    specificGuidValue: string;
};

export type GeneralDefaultServerPreview = {
    mode: WasteMovementDefaultMode;
    status: string;
    resolvedDisplay?: string | null;
};

/** Fields whose Specific value dropdown shows code only (no description). */
export const GENERAL_DEFAULT_CODE_ONLY_SELECT_FIELDS = new Set([
    "MunicipalFlagId",
    "DegradableFlagId",
    "RegSourceActivityId",
    "RegPreTreatmentId",
    "RegPhysicalStateId",
]);

export function isGeneralDefaultCodeOnlySelectField(fieldKey: string): boolean {
    return GENERAL_DEFAULT_CODE_ONLY_SELECT_FIELDS.has(fieldKey);
}

export function stripDescriptionFromResolvedDisplay(display: string): string {
    const sep = " — ";
    const idx = display.indexOf(sep);
    if (idx >= 0) return display.slice(0, idx).trim();
    return display.trim();
}

export function formatGeneralDefaultResolvedCode(fieldKey: string, code: string): string {
    if (fieldKey === "RegEwcCodeId") return formatEwcDisplayCode(code);
    return code;
}

export function computeGeneralDefaultPreview(
    draft: GeneralDefaultPreviewInput,
    items: ReadonlyArray<GeneralDefaultLookupItem>,
    serverPreview?: GeneralDefaultServerPreview | null
): { status: string; resolvedDisplay: string | null } {
    if (draft.mode === "None") {
        return { status: "None", resolvedDisplay: null };
    }

    if (draft.mode === "SpecificValue") {
        if (!draft.specificGuidValue) {
            return { status: "None", resolvedDisplay: null };
        }
        const item = items.find((i) => i.id === draft.specificGuidValue);
        if (!item) {
            return { status: "NotAppliedInvalid", resolvedDisplay: null };
        }
        return {
            status: "Applied",
            resolvedDisplay: formatGeneralDefaultResolvedCode(draft.fieldKey, item.code),
        };
    }

    if (serverPreview?.mode === "MostFrequent") {
        const status = serverPreview.status;
        const resolvedDisplay =
            status === "Applied" && serverPreview.resolvedDisplay
                ? stripDescriptionFromResolvedDisplay(serverPreview.resolvedDisplay)
                : null;
        return { status, resolvedDisplay: formatResolvedDisplayForField(draft.fieldKey, resolvedDisplay) };
    }

    return { status: "NoValueYet", resolvedDisplay: null };
}

export function mostFrequentPreviewFromApi(
    fieldKey: string,
    status: string,
    items: ReadonlyArray<GeneralDefaultLookupItem>,
    resolvedGuidValue?: string | null,
    resolvedDisplay?: string | null
): { status: string; resolvedDisplay: string | null } {
    if (status !== "Applied") {
        return { status, resolvedDisplay: null };
    }

    if (resolvedGuidValue) {
        const item = items.find((i) => i.id === resolvedGuidValue);
        if (item) {
            return {
                status,
                resolvedDisplay: formatGeneralDefaultResolvedCode(fieldKey, item.code),
            };
        }
    }

    if (resolvedDisplay) {
        return {
            status,
            resolvedDisplay: formatResolvedDisplayForField(
                fieldKey,
                stripDescriptionFromResolvedDisplay(resolvedDisplay)
            ),
        };
    }

    return { status, resolvedDisplay: null };
}

function formatResolvedDisplayForField(fieldKey: string, display: string | null): string | null {
    if (!display) return null;
    if (fieldKey === "RegEwcCodeId") return formatEwcDisplayCode(display);
    return display;
}

export function normalizeServerGeneralDefaultPreview(
    fieldKey: string,
    mode: WasteMovementDefaultMode,
    status: string,
    resolvedDisplay?: string | null
): { status: string; resolvedDisplay: string | null } {
    if (status !== "Applied" || !resolvedDisplay) {
        return { status, resolvedDisplay: null };
    }
    return {
        status,
        resolvedDisplay: formatGeneralDefaultResolvedCode(
            fieldKey,
            stripDescriptionFromResolvedDisplay(resolvedDisplay)
        ),
    };
}
